import { ReactNode, useMemo } from 'react';
import styled from 'styled-components';

// components
import {
  U21Alert,
  U21Button,
  U21ButtonGroup,
  U21Loading,
  U21NoData,
  U21Spacer,
  U21Timeline,
  U21Typography,
} from 'app/shared/u21-ui/components';

// queries
import { AuditTrailEntry } from 'app/modules/auditService/components/AuditTrailEntry';
import { useAuditServiceList } from 'app/modules/auditService/queries/useAuditServiceList';

// types
import {
  AuditTrailAssetType,
  AuditServiceEntry,
} from 'app/modules/auditService/types';
import { getFormattedAssetName } from 'app/modules/auditService/constants/auditTrailStrings';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import { useLocalStorageState } from 'app/shared/hooks/useLocalStorage';
import { formatDate } from 'app/shared/utils/date';

// We can not show any action taken after this date
export const FIRST_AUDIT_ENTRY_TIMESTAMP = new Date('2023-07-01');

// Asset types with view_detail events in audit-service ViewActions.
// When fincen_ctr.view_detail ships: add 'fincen_ctr' (see fincen_ctr.view_detail in auditTrailStrings).
const VIEW_EVENT_ASSET_TYPES: AuditTrailAssetType[] = ['alert', 'case', 'sar'];

const isViewDetailAction = (action: string) => action.endsWith('.view_detail');

interface Props {
  assetType: AuditTrailAssetType;
  assetId: string;
  // Optional prop to show a warning if the asset was created before the first audit entry
  assetCreatedAt?: string;
}

/** A filter function to be use for an
 * array of `AuditServiceEntry`. This improves
 * the UI/UX of audit trails by preventing long
 * contiguous audits of the same action, such
 * as narrative edits (since they auto save).
 * If elements n and n+1 are both narrative edit
 * audits, remove n+1, for all n.
 */
const auditLogCompressor = (
  currentLog: AuditServiceEntry,
  index: number,
  array: AuditServiceEntry[],
): boolean => {
  /* Since we need a window size of 2 and
   * we look "back" to make the window, the first
   * element is _always_ carried through
   */
  if (index === 0) {
    return true;
  }

  /* If the current element and previous element are
   * one of the "filterable" audits, filter out the
   * current element
   */
  if (
    currentLog.action === 'alert.narrative_edit' &&
    array[index - 1].action === 'alert.narrative_edit'
  ) {
    return false;
  }
  if (
    currentLog.action === 'case.narrative_edit' &&
    array[index - 1].action === 'case.narrative_edit'
  ) {
    return false;
  }
  const prev = array[index - 1];
  if (
    isViewDetailAction(currentLog.action) &&
    isViewDetailAction(prev.action) &&
    currentLog.actor.id === prev.actor.id &&
    currentLog.actor.type === prev.actor.type
  ) {
    return false;
  }

  // Passing all if guards means it can stay in the array
  return true;
};

export const AuditTrail = ({ assetType, assetId, assetCreatedAt }: Props) => {
  const [showViewEvents, setShowViewEvents] = useLocalStorageState<boolean>(
    LocalStorageKeys.AUDIT_TRAIL_SHOW_VIEW_EVENTS,
    false,
  );
  // audit-service: exclude_noisy_events=true keeps view_detail but drops view_summary/snapshot.
  // exclude_noisy_events=false (default) hides all view events server-side.
  const excludeNoisyEvents =
    VIEW_EVENT_ASSET_TYPES.includes(assetType) && showViewEvents;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status } =
    useAuditServiceList(assetType, assetId, excludeNoisyEvents);

  /* This has to come _first_ before any rendering, the
   * early returning messes with react.
   */
  const compressedAuditLogs = useMemo<AuditServiceEntry[]>(() => {
    if (data?.pages === undefined) {
      return [];
    }
    // This removes _some_ audit logs for readability.
    // Read the function for more details
    const logs = data.pages
      .reduce<AuditServiceEntry[]>(
        (acc, currPage) => acc.concat(currPage.auditLogs),
        [],
      )
      .filter(auditLogCompressor);
    // exclude_noisy_events=true returns view_detail; Actions only relies on API default (no param).
    return showViewEvents
      ? logs
      : logs.filter((log) => !isViewDetailAction(log.action));
  }, [data, showViewEvents]);

  const showViewEventsToggle = VIEW_EVENT_ASSET_TYPES.includes(assetType);

  const auditTrailWarningAlert: ReactNode = useMemo(() => {
    if (assetCreatedAt) {
      const assetCreatedBeforeFirstAuditRecord =
        new Date(assetCreatedAt) < FIRST_AUDIT_ENTRY_TIMESTAMP;
      if (assetCreatedBeforeFirstAuditRecord) {
        // wrap in an extra div - U21Alert has min-height 0 for collapsing
        // which causes Timeline to squeeze this if there is enough content
        return (
          <div>
            <U21Alert color="warning">
              The {getFormattedAssetName(assetType)} was created before{' '}
              {formatDate(FIRST_AUDIT_ENTRY_TIMESTAMP)}. The audit trail may not
              show all actions taken, if you need historical audits reach out to
              Unit21 Support.
            </U21Alert>
          </div>
        );
      }
    }
    return null;
  }, [assetCreatedAt, assetType]);

  if (status === 'pending') return <U21Loading loading />;
  if (!data || (data.pages[0]?.auditLogs.length === 0 && !hasNextPage))
    return (
      <U21Spacer>
        {auditTrailWarningAlert}
        <U21NoData />
      </U21Spacer>
    );

  return (
    <U21Spacer>
      {auditTrailWarningAlert}
      {showViewEventsToggle && (
        <ToggleRow>
          <U21ButtonGroup<boolean>
            size="small"
            buttons={[
              { label: 'Actions only', value: false },
              { label: 'All events', value: true },
            ]}
            onClick={setShowViewEvents}
            value={showViewEvents}
          />
        </ToggleRow>
      )}
      <U21Timeline>
        {compressedAuditLogs.map((entry) => (
          <AuditTrailEntry key={entry.submittedAt} {...entry} />
        ))}
      </U21Timeline>
      <Footer>
        {hasNextPage ? (
          <U21Button
            color="primary"
            loading={isFetchingNextPage}
            onClick={() => !isFetchingNextPage && fetchNextPage()}
            variant="text"
          >
            Show more
          </U21Button>
        ) : (
          <U21Typography
            color="text.secondary"
            role="status"
            aria-live="polite"
          >
            This is the end of the audit trail
          </U21Typography>
        )}
      </Footer>
    </U21Spacer>
  );
};

const ToggleRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
`;
